import { AlertCircle, CheckCircle2, Loader2, PackageOpen, Search, ShoppingCart } from 'lucide-react'
import React, { useEffect, useRef, useState } from 'react'
import { useHotkeys } from 'react-hotkeys-hook'

import { Button } from '@/app/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/app/components/ui/dialog'
import { Input } from '@/app/components/ui/input'
import { posProductService, type Product } from '@/app/services/pos.service'
import { isPromotionPricing, pricingRuleLabel, pricingRuleName } from '@/app/lib/pricing-display'

export interface AddedProductNotice {
  name: string
  sequence: number
}

export interface PriceCheckModalProps {
  isOpen: boolean
  onClose: () => void
  onAddToCart: (product: Product) => void
  currentSucursalId?: number
  cartQuantity: number
  addedProductNotice: AddedProductNotice | null
}

export function PriceCheckModal({
  isOpen,
  onClose,
  onAddToCart,
  cartQuantity,
  addedProductNotice,
}: PriceCheckModalProps) {
  const [searchTerm, setSearchTerm] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [results, setResults] = useState<Product[]>([])
  const [totalResults, setTotalResults] = useState(0)
  const [hasSearched, setHasSearched] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [confirmation, setConfirmation] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const requestRef = useRef<AbortController | null>(null)
  const seenNoticeSequenceRef = useRef(addedProductNotice?.sequence ?? 0)
  const wasOpenRef = useRef(false)

  useEffect(() => {
    let focusTimer: ReturnType<typeof setTimeout> | undefined

    if (isOpen && !wasOpenRef.current) {
      wasOpenRef.current = true
      setSearchTerm('')
      setResults([])
      setTotalResults(0)
      setHasSearched(false)
      setErrorMsg(null)
      setConfirmation(null)
      seenNoticeSequenceRef.current = addedProductNotice?.sequence ?? 0
      focusTimer = setTimeout(() => {
        inputRef.current?.focus()
      }, 100)
    } else if (!isOpen) {
      wasOpenRef.current = false
      requestRef.current?.abort()
    }

    return () => {
      if (focusTimer) clearTimeout(focusTimer)
    }
  }, [addedProductNotice?.sequence, isOpen])

  useEffect(() => {
    if (!isOpen || !addedProductNotice) return
    if (addedProductNotice.sequence === seenNoticeSequenceRef.current) return

    seenNoticeSequenceRef.current = addedProductNotice.sequence
    setConfirmation(`${addedProductNotice.name} se agregó al carrito.`)
    inputRef.current?.focus()
    inputRef.current?.select()
  }, [addedProductNotice, isOpen])

  const searchProducts = async () => {
    const normalizedSearch = searchTerm.trim()
    if (!normalizedSearch) return

    requestRef.current?.abort()
    const controller = new AbortController()
    requestRef.current = controller
    setIsLoading(true)
    setErrorMsg(null)
    setConfirmation(null)
    setHasSearched(true)

    try {
      const response = await posProductService.getProducts({
        search: normalizedSearch,
        per_page: 20,
      }, controller.signal)
      setResults(response.data)
      setTotalResults(Number(response.total ?? response.data.length))
    } catch (error: unknown) {
      const errorName = error instanceof Error ? error.name : ''
      if (errorName === 'CanceledError' || errorName === 'AbortError') return
      setResults([])
      setTotalResults(0)
      setErrorMsg('No fue posible consultar los productos. Intenta nuevamente.')
    } finally {
      if (requestRef.current === controller) {
        setIsLoading(false)
        requestRef.current = null
      }
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    void searchProducts()
  }

  useHotkeys('esc', () => {
    if (isOpen) {
      onClose()
    }
  }, { enableOnFormTags: true }, [isOpen, onClose])

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="flex max-h-[calc(100dvh-1rem)] min-h-0 w-[calc(100vw-1rem)] flex-col gap-3 overflow-hidden p-4 sm:max-w-2xl sm:p-6">
        <DialogHeader>
          <DialogTitle>Consulta rápida de productos</DialogTitle>
          <DialogDescription>
            Busca por nombre, sustancia activa, SKU o código de barras.
          </DialogDescription>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 flex-col gap-3">
          <form onSubmit={handleSubmit} className="flex gap-2">
            <div className="relative min-w-0 flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                ref={inputRef}
                type="search"
                aria-label="Buscar productos"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Nombre, sustancia o código..."
                className="pl-9"
                disabled={isLoading}
              />
            </div>
            <Button type="submit" disabled={isLoading || !searchTerm.trim()}>
              {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Buscar'}
            </Button>
          </form>

          <div aria-live="polite" aria-atomic="true">
            {confirmation && (
              <div className="flex items-start gap-2 rounded-lg border border-green-200 bg-green-50 p-3 text-green-800">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
                <p className="text-sm font-medium">{confirmation} El carrito contiene {cartQuantity} unidades.</p>
              </div>
            )}
          </div>

          {errorMsg && (
            <div className="flex items-start gap-3 rounded-lg border border-orange-200 bg-orange-50 p-4 text-orange-800">
              <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
              <p className="text-sm font-medium">{errorMsg}</p>
            </div>
          )}

          {!isLoading && hasSearched && !errorMsg && results.length === 0 && (
            <div className="flex flex-1 flex-col items-center justify-center rounded-lg border border-dashed border-slate-300 px-4 py-10 text-center">
              <PackageOpen className="mb-2 h-8 w-8 text-slate-400" />
              <p className="font-medium text-slate-700">No se encontraron productos.</p>
              <p className="mt-1 text-sm text-slate-500">Prueba con otro nombre, sustancia o código.</p>
            </div>
          )}

          {results.length > 0 && (
            <div className="flex min-h-0 flex-1 flex-col gap-2">
              <p className="text-xs text-slate-500">
                {totalResults > results.length
                  ? `Mostrando ${results.length} de ${totalResults} resultados. Refina la búsqueda para ver otros productos.`
                  : `${totalResults} ${totalResults === 1 ? 'resultado' : 'resultados'}.`}
              </p>
              <div className="min-h-0 flex-1 space-y-2 overflow-y-auto pr-1">
                {results.map((product) => (
                  <article key={product.id} className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <h3 className="font-semibold text-slate-900">{product.name}</h3>
                        {product.description && (
                          <p className="mt-1 line-clamp-2 text-xs text-slate-600">{product.description}</p>
                        )}
                        <p className="mt-1 truncate font-mono text-xs text-slate-500">{product.code || 'Sin código'}</p>
                      </div>
                      <div className="shrink-0 text-right">
                        {Number(product.amounts?.regular_unit_total || product.price) > Number(product.price) && (
                          <p className="text-xs text-slate-500 line-through">${Number(product.amounts?.regular_unit_total).toFixed(2)}</p>
                        )}
                        <p className="font-bold text-slate-900">${Number(product.price || 0).toFixed(2)}</p>
                      </div>
                    </div>
                    {isPromotionPricing(product.pricing) && (
                      <div className="mt-2 rounded-md border border-purple-200 bg-purple-50 px-2.5 py-2 text-xs text-purple-800">
                        <p className="font-semibold">{pricingRuleName(product.pricing)}</p>
                        <p>{pricingRuleLabel(product.pricing)}</p>
                      </div>
                    )}
                    <div className="mt-3 flex items-center justify-between gap-3 border-t border-slate-100 pt-3">
                      <span className="text-xs text-slate-600">Existencia: {product.stock || 0}</span>
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => onAddToCart(product)}
                        aria-label={`Agregar ${product.name} al carrito`}
                      >
                        <ShoppingCart className="mr-1.5 h-4 w-4" />
                        Agregar
                      </Button>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
